import React, { useCallback, useEffect, useMemo } from "react";
import { actions as sharedActions, selectors as sharedSelectors } from "@shared/store";
import { actions as ClientActions, actions, selectors as ClientSelectors } from "@containers/Contact/store";
import { actions as AccountActions, selectors as AccountSelectors } from "@containers/Account/store";
import { MODAL_TYPES } from "@shared/constants";
import { Account, RoleName, User } from "@shared/models";
import { CreateClientDto } from "@containers/Contact/interfaces";
import { useDispatch, useSelector } from "react-redux";
import { ClientForm } from "@containers/Contact/components/ClientCreateEditContainer/ClientForm/ClientForm";
import { actions as authActions } from "@containers/Auth/store";
import { checkIfAdmin } from "@shared/utils/ACL";

interface ClientCreateEditContainerProps {
  onClose: () => void;
  client?: User;
  callback?: () => void;
  contact?: Account;
}

const ClientCreateEditContainer = (props: ClientCreateEditContainerProps) => {
  const { onClose, client, callback } = props;
  const dispatch = useDispatch();

  const selectedClient = useSelector(ClientSelectors.getSelectedClient());
  const selectedContact = useSelector(AccountSelectors.getSelectedAccount());

  useEffect(() => {
    if (client && !selectedClient) {
      dispatch(ClientActions.getClient.request({ id: client.id }));
    }
  }, [dispatch, client, selectedClient]);

  useEffect(() => {
    return () => {
      dispatch(ClientActions.clearClient());
      dispatch(ClientActions.clearStates());
      dispatch(ClientActions.clearCounties());
    };
  }, [dispatch]);

  const changeFormValues = useCallback(() => {
    dispatch(sharedActions.updateOverlay({ leaveConfirm: true }));
  }, [dispatch]);

  const currentUser = useSelector(sharedSelectors.getUserDetails());

  const logout = useCallback(() => {
    dispatch(authActions.clearFilledEmail());
    dispatch(authActions.logout.request({ withApiRequest: true }));
  }, [dispatch]);

  const handleSubmitForm = useCallback(
    (payload: Partial<CreateClientDto>) => {
      if (!client) {
        return dispatch(
          actions.createClient.request({
            ...payload,
            callback: () => {
              dispatch(sharedActions.hideOverlay());
              callback?.();
            },
          }),
        );
      }

      dispatch(
        actions.updateClient.request({
          ...payload,
          id: Number(client.id),
          callback: () => {
            if (
              currentUser?.id === client?.id &&
              checkIfAdmin(currentUser) &&
              !payload?.roles?.includes(RoleName.admin)
            ) {
              dispatch(sharedActions.hideOverlay());
              logout();
            } else {
              dispatch(sharedActions.hideOverlay());
              callback?.();
            }
          },
        }),
      );
    },
    [dispatch, client, callback, currentUser, logout],
  );

  const isSelectedClientActiveInContact = useMemo(() => {
    return !!selectedContact?.users?.some((user) => {
      return user.is_active_in_account && user.id === selectedClient?.id;
    });
  }, [selectedContact, selectedClient?.id]);

  const changeClientAccountStatus = useCallback(() => {
    if (!client || !selectedContact) {
      return;
    }
    dispatch(
      ClientActions.changeClientAccountStatus.request({
        user_id: client?.id,
        account_id: selectedContact?.id,
        is_active: !isSelectedClientActiveInContact,
        callback: () => dispatch(AccountActions.getAccount.request({ id: selectedContact.id })),
      }),
    );
  }, [client, selectedContact, dispatch, isSelectedClientActiveInContact]);

  const handleChangeClientAccountStatus = useCallback(() => {
    if (!client) return;

    const changeStatusActionName = isSelectedClientActiveInContact ? "Deactivate" : "Activate";

    dispatch(
      sharedActions.showModal({
        type: MODAL_TYPES.CONFIRM,
        props: {
          heading: `${changeStatusActionName} User`,
          content: isSelectedClientActiveInContact
            ? "When you deactivate this user, they will lose access to the Dataflik web application."
            : "When you activate this user, you will grant them access to the Dataflik web application.",
          cancelBtnStyle: "blue-bordered",
          cancelBtnText: "Cancel",
          successBtnText: changeStatusActionName,
          successBtnStyle: "accent-green",
          onSuccess: () => {
            changeClientAccountStatus();
          },
        },
      }),
    );
  }, [changeClientAccountStatus, isSelectedClientActiveInContact, client, dispatch]);

  if (!selectedContact) {
    return null;
  }

  return (
    <div>
      <ClientForm
        user={selectedClient}
        account={selectedContact}
        changeClientAccountStatus={handleChangeClientAccountStatus}
        isSelectedClientActiveInContact={isSelectedClientActiveInContact}
        onChangeForm={changeFormValues}
        onCloseForm={onClose}
        onSubmitForm={handleSubmitForm}
      />
    </div>
  );
};

export default ClientCreateEditContainer;
